import { useEffect, useState, useCallback } from 'react';
import type { User } from '../types/usersDemo';
import { getUsers } from '../services/users';

export function useUsersData() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { users, loading, error, refetch: fetchUsers };
}

export default useUsersData;

import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toaster } from "baseui/toast";
import { 
  useGetUser, 
  usePostUser,
  usePutUserId,
  usePatchUserId,
  useDeleteUserId
} from "../api/generated";
import type { User as GeneratedUser } from "../api/generated";

// Extended params interface with additional filter options
interface UseUsersParams {
  page?: number;
  limit?: number;
  name?: string; // Legacy - kept for backward compatibility
  
  // Search filters using _search suffix per memory
  name_search?: string;
  email_search?: string;
  phone_search?: string;
  
  // Status filters using _in suffix per memory
  owner_resident_status?: string[];
  owner_resident_status_in?: string[];
  
  // User type IDs: ['2'] = Owner, ['3'] = Resident, ['4'] = Property Manager
  user_type?: string[];
  user_type_in?: string[];
  
  // Date range filters
  created_at_gte?: string; // Date greater than or equal
  created_at_lte?: string; // Date less than or equal
  
  enabled?: boolean; // Control whether the query should run
}

// Custom hook for Users with pagination, search and filtering
export const useUsers = (params?: UseUsersParams) => {
  const queryClient = useQueryClient();
  
  // Separate enabled from API parameters
  const { enabled = true, ...apiParams } = params || {};
  
  // Build query parameters manually to avoid array serialization issues
  const queryParams: any = {
    page: apiParams?.page || 1,
    limit: apiParams?.limit || 20,
  };

  // Legacy name parameter - convert to name_search for backward compatibility
  if (apiParams?.name && apiParams.name.trim() !== '') {
    queryParams.name_search = apiParams.name;
  }

  // Add search parameters using _search suffix per memory
  if (apiParams?.name_search && apiParams.name_search.trim() !== '') {
    queryParams.name_search = apiParams.name_search;
  }
  
  if (apiParams?.email_search && apiParams.email_search.trim() !== '') {
    queryParams.email_search = apiParams.email_search;
  }
  
  if (apiParams?.phone_search && apiParams.phone_search.trim() !== '') {
    queryParams.phone_search = apiParams.phone_search;
  }

  // Add owner_resident_status_in as comma-separated string per memory
  const statusArray = apiParams?.owner_resident_status_in || apiParams?.owner_resident_status;
  if (statusArray && statusArray.length > 0) {
    queryParams.owner_resident_status_in = statusArray.join(',');
  }

  // Add user_type_in as comma-separated string per memory
  const userTypeArray = apiParams?.user_type_in || apiParams?.user_type;
  if (userTypeArray && userTypeArray.length > 0) {
    queryParams.user_type_in = userTypeArray.join(',');
  }

  // Add date range filters
  if (apiParams?.created_at_gte) {
    queryParams.created_at_gte = apiParams.created_at_gte;
  }
  
  if (apiParams?.created_at_lte) {
    queryParams.created_at_lte = apiParams.created_at_lte;
  }

  const { data: usersResponse, isLoading, error } = useGetUser(queryParams, {
    query: {
      enabled, // Use the separated enabled parameter
    }
  });



  
  
  // Extract users data from response (same format as estates)
  const users: User[] = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
  
  // Extract pagination metadata from response (same format as estates)
  const totalPages = (usersResponse as any)?.total_pages || 1;
  const currentPage = (usersResponse as any)?.page || params?.page || 1;
  const totalUsers = (usersResponse as any)?.total_elements || 0;
  
  const createUserMutation = usePostUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/user'] });
        toaster.positive("User created successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error creating user:', error);
        toaster.negative("Error creating User", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const updateUserMutation = usePutUserId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/user'] });
        toaster.positive("User updated successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error updating user:', error);
        toaster.negative("Error updating User", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const patchUserMutation = usePatchUserId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/user'] });
        toaster.positive("User updated successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error updating user:', error);
        toaster.negative("Error updating User", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  const deleteUserMutation = useDeleteUserId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/user'] });
        toaster.positive("User deleted successfully", {
          autoHideDuration: 3000,
        });
      },
      onError: (error: any) => {
        console.error('Error deleting user:', error);
        toaster.negative("Error deleting User", {
          autoHideDuration: 3000,
        });
      },
    },
  });

  return {
    users,
    isLoading,
    error,
    // Pagination data (same format as estates)
    totalPages,
    currentPage,
    totalUsers,
    createUser: (data: User) => createUserMutation.mutate({ data }),
    updateUser: (id: string, data: User) => updateUserMutation.mutate({ id, data }),
    patchUser: (id: string, data: Partial<User>) => patchUserMutation.mutate({ id, data: data as User }),
    deleteUser: (id: string) => deleteUserMutation.mutate({ id }),
    isCreating: createUserMutation.isPending,
    isUpdating: updateUserMutation.isPending || patchUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending,
  };
}; 